/**
 * Re-fetch a query on an interval while `active`, for at most `limitMs`.
 *
 * The AI panels wait on a Lambda that usually answers in seconds and
 * occasionally never (a cap, a guardrail): poll the owning query while
 * its state is `pending`, and stop after a minute rather than forever.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export const POLL_MS = 3000;
export const POLL_LIMIT_MS = 60_000;

export function useBoundedPoll(
  active: boolean,
  queryKey: unknown[],
  { intervalMs = POLL_MS, limitMs = POLL_LIMIT_MS } = {},
): void {
  const queryClient = useQueryClient();
  const start = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      start.current = null;
      return;
    }
    start.current ??= Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - (start.current ?? Date.now()) > limitMs) {
        window.clearInterval(timer);
        return;
      }
      void queryClient.invalidateQueries({ queryKey });
    }, intervalMs);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
